// src/pages/AmalanBaikDashboard.jsx
import React, { useEffect, useState, Fragment } from "react";
import { toast } from "react-toastify";
import API from "../../services/API";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";

// ==================== API HELPERS ====================
const fetchAmalanSummary = (params = {}) =>
    API.get("/analytics/amalanBaik/summary", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanCategorySummary = (params = {}) =>
    API.get("/analytics/amalanBaik/byCategory", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanPendingStudents = (params = {}) =>
    API.get("/analytics/amalanBaik/pendingByStudent", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

const fetchAmalanTopOverall = (params) =>
    API.get("/analytics/amalanBaik/topStudents", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });
const fetchAmalanTopTeachersAllTime = (params) =>
    API.get("/goodDeeds/topFiveTeachersAmalanBaik", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });
const fetchAmalanTopStudentsMonth = (params = {}) =>
    API.get("/goodDeeds/topFiveStudentsAmalanBaik", {
        headers: { token: localStorage.getItem("token") },
        params: { is_portal: true, ...params },
    });

// ==================== COLORS ====================
// Inspired by MySP.ai vibrant palette (bright but balanced)
const COLORS = [
    "#6366F1", // Indigo
    "#22C55E", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#0EA5E9", // Sky Blue
    "#8B5CF6", // Violet
    "#EC4899", // Pink
    "#14B8A6", // Teal
    "#F97316", // Orange
    "#84CC16", // Lime
    "#3B82F6", // Blue
    "#10B981", // Green
    "#EAB308", // Yellow
    "#D946EF", // Fuchsia
    "#06B6D4", // Cyan
];


// ==================== COMPONENT ====================
const AmalanBaikDashboard = () => {
    const today = new Date();
    const currentMonth = today.toISOString().slice(0, 7);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);

    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [topStudents, setTopStudents] = useState([]);
    const [topTeachers, setTopTeachers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pendingStudents, setPendingStudents] = useState([]);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [topOverallStudents, setTopOverallStudents] = useState([]);
    const [topOverallTeachers, setTopOverallTeachers] = useState([]);

    // 🔹 Hide "Attendance" category by default
    const filteredCategories = categories.filter(c => c.category?.toLowerCase() !== "attendance");


    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const resSummary = await fetchAmalanSummary({ month: selectedMonth });
                if (resSummary.data.response_code === 1000) {
                    setOverview(resSummary.data.data.overview);
                    setTrend(resSummary.data.data.trend || []);
                } else {
                    toast.error(resSummary.data.message || "Failed to load summary");
                }

                const resCategories = await fetchAmalanCategorySummary({ month: selectedMonth });
                if (resCategories.data.response_code === 1000) setCategories(resCategories.data.data || []);

                const resPending = await fetchAmalanPendingStudents({ month: selectedMonth, limit_top: 10 });
                if (resPending.data.response_code === 1000) setPendingStudents(resPending.data.data || []);
                try {
                    const resTopTeachers = await fetchAmalanTopTeachersAllTime({ limit_top: 5 });

                    if (resTopTeachers.data.response_code === 1000)
                        setTopOverallTeachers(resTopTeachers.data.data || []);
                } catch (err) {
                    console.error("❌ Failed to fetch top teachers:", err);
                }
                try {
                    const resTopOverall = await fetchAmalanTopStudentsMonth({
                        scope: "month",
                        month: selectedMonth,
                        limit_top: 5
                    });

                    if (resTopOverall.data.response_code === 1000) {
                        setTopOverallStudents(resTopOverall.data.data || []);
                    }
                } catch (err) {
                    console.error("❌ Failed to fetch top monthly students:", err);
                }

            } catch (err) {
                toast.error(err?.message || "Failed to fetch Amalan Baik analytics");
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [selectedMonth]);

    return (
        <Fragment>
            <div className="container-fluid">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col d-flex align-items-center">
                        <h2 className="fw-bold text-danger">Amalan Baik Dashboard</h2>
                    </div>
                    <div className="col-auto">
                        <input
                            type="month"
                            className="form-control"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        />
                    </div>
                </div>

                {/* Overview */}
                {overview && (
                    <div className="row mb-4">
                        {[
                            { label: "Total Amalan Baik", value: overview.total_ops },
                            { label: "Students", value: overview.unique_students },
                            { label: "Teachers", value: overview.unique_teachers },
                            { label: "Transferred", value: overview.transferred },
                            { label: "Pending", value: overview.pending },
                            { label: "Transfer Rate", value: `${overview.transfer_rate_pct}%` },
                        ].map((item, idx) => (
                            <div key={idx} className="col-6 col-md-4 col-lg-2 mb-3">
                                <div className="card text-center border-0 shadow-sm h-100 rounded-4">
                                    <div className="card-body">
                                        <h4 className="fw-bold mb-1">{item.value}</h4>
                                        <p className="text-muted small mb-0">{item.label}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* 🏆 Overall Top 5 Students (All Time) */}
                {topOverallStudents.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4 p-4">
                        <h5 className="mb-4 text-center fw-bold text-danger">
                            Overall Top 5 Students for the month
                        </h5>

                        {/* Podium Section */}
                        <div className="d-flex justify-content-center align-items-end gap-4 flex-wrap mb-5">
                            {[
                                topOverallStudents[1],
                                topOverallStudents[0],
                                topOverallStudents[2],
                            ].map((s, idx) => {
                                if (!s) return null;
                                const rank = idx === 0 ? 2 : idx === 1 ? 1 : 3;

                                const heights = { 1: "220px", 2: "180px", 3: "150px" };
                                const colors = {
                                    1: "linear-gradient(180deg, #fffbe6, #fde047)", // Gold
                                    2: "linear-gradient(180deg, #f9fafb, #d1d5db)", // Silver
                                    3: "linear-gradient(180deg, #fff7ed, #f59e0b)", // Bronze
                                };
                                const borders = {
                                    1: "#eab308",
                                    2: "#9ca3af",
                                    3: "#b45309",
                                };
                                const medalText = { 1: "🥇 1st", 2: "🥈 2nd", 3: "🥉 3rd" };

                                return (
                                    <div
                                        key={s.student_uuid || idx}
                                        className="d-flex flex-column align-items-center justify-content-end rounded-4 shadow-sm position-relative p-3"
                                        style={{
                                            background: colors[rank],
                                            border: `2px solid ${borders[rank]}`,
                                            height: heights[rank],
                                            width: "180px",
                                            color: "#111827",
                                            transition: "all 0.3s ease",
                                            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-6px)";
                                            e.currentTarget.style.boxShadow =
                                                "0 8px 24px rgba(0,0,0,0.15)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow =
                                                "0 4px 10px rgba(0,0,0,0.1)";
                                        }}
                                    >
                                        {/* Rank Header */}
                                        <div
                                            className="fw-bold text-center rounded-pill px-3 py-1 mb-2"
                                            style={{
                                                background: borders[rank],
                                                color: "white",
                                                fontSize: "0.9rem",
                                                letterSpacing: "0.5px",
                                            }}
                                        >
                                            {medalText[rank]}
                                        </div>

                                        {/* Student Info */}
                                        <div className="text-center mt-auto">
                                            <div
                                                className="fw-bold text-uppercase"
                                                style={{
                                                    fontSize: "0.95rem",
                                                    lineHeight: 1.3,
                                                    color: "#111827",
                                                }}
                                            >
                                                {s.student_name}
                                            </div>
                                            <div className="text-muted small">{s.class_name}</div>
                                            <div
                                                className="fw-bold mt-1"
                                                style={{ fontSize: "1.5rem" }}
                                            >
                                                {s.total_deeds} PTS
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Remaining 4th–5th */}
                        <div className="d-flex flex-column gap-2">
                            {topOverallStudents.slice(3).map((s, idx) => (
                                <div
                                    key={s.student_uuid || idx}
                                    className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light shadow-sm"
                                >
                                    <div className="text-black">
                                        <strong className="text-black">#{idx + 4}</strong> {s.student_name}
                                        <div className="text-muted small">{s.class_name}</div>
                                    </div>
                                    <div className="fw-bold text-danger">{s.amalan_baik_count} pts</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* 🧑‍🏫 Top 5 Teachers (All Time) */}
                {topOverallTeachers.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4 p-4">
                        <h5 className="mb-4 text-center fw-bold text-danger">
                            Top 5 Teachers (All Time)
                        </h5>

                        <div className="d-flex justify-content-center align-items-end gap-4 flex-wrap mb-5">
                            {[
                                topOverallTeachers[1],
                                topOverallTeachers[0],
                                topOverallTeachers[2],
                            ].map((t, idx) => {
                                if (!t) return null;
                                const rank = idx === 0 ? 2 : idx === 1 ? 1 : 3;

                                const heights = { 1: "200px", 2: "160px", 3: "140px" };
                                const colors = {
                                    1: "linear-gradient(180deg, #fffbe6, #fde047)", // Gold
                                    2: "linear-gradient(180deg, #f9fafb, #d1d5db)", // Silver
                                    3: "linear-gradient(180deg, #fff7ed, #f59e0b)", // Bronze
                                };
                                const borders = {
                                    1: "#eab308",
                                    2: "#9ca3af",
                                    3: "#b45309",
                                };
                                const medalText = { 1: "🥇 1st", 2: "🥈 2nd", 3: "🥉 3rd" };

                                return (
                                    <div
                                        key={t.teacher_id || idx}
                                        className="d-flex flex-column align-items-center justify-content-end rounded-4 shadow-sm position-relative p-3"
                                        style={{
                                            background: colors[rank],
                                            border: `2px solid ${borders[rank]}`,
                                            height: heights[rank],
                                            width: "180px",
                                            color: "#111827",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <div
                                            className="fw-bold text-center rounded-pill px-3 py-1 mb-2"
                                            style={{
                                                background: borders[rank],
                                                color: "white",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            {medalText[rank]}
                                        </div>

                                        <div className="text-center mt-auto">
                                            <div
                                                className="fw-bold text-uppercase"
                                                style={{
                                                    fontSize: "0.95rem",
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {t.teacher_name}
                                            </div>
                                            <div
                                                className="fw-bold mt-1 text-danger"
                                                style={{ fontSize: "1.5rem" }}
                                            >
                                                {t.total_deeds} PTS
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="d-flex flex-column gap-2">
                            {topOverallTeachers.slice(3).map((t, idx) => (
                                <div
                                    key={t.teacher_id || idx}
                                    className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light shadow-sm"
                                >
                                    <div className="text-black">
                                        <strong className="text-black">#{idx + 4}</strong> {t.teacher_name}
                                    </div>
                                    <div className="fw-bold text-danger">{t.total_deeds} pts</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                {/* Trend */}
                {trend.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-header bg-light rounded-top-4">
                            <h5 className="mb-0 text-danger">Trend ({selectedMonth})</h5>
                        </div>
                        <div className="card-body">
                            <ResponsiveContainer width="100%" height={350}>
                                <LineChart data={trend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="total" stroke="#3B82F6" name="Total Amalan Baik" />
                                    <Line type="monotone" dataKey="transferred" stroke="#10B981" name="Transferred" />
                                    <Line type="monotone" dataKey="pending" stroke="#EF4444" name="Pending" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
                {/* Category Breakdown */}
                {filteredCategories.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-header bg-light rounded-top-4 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-danger">Category Breakdown</h5>
                            {selectedCategory && (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setSelectedCategory(null)}
                                >
                                    Clear Filter
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            <ResponsiveContainer width="100%" height={350}>
                                <PieChart>
                                    <Pie
                                        data={filteredCategories}
                                        dataKey="total"
                                        nameKey="category"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={110}
                                        label
                                        onClick={(data) => {
                                            if (selectedCategory === data.category) {
                                                setSelectedCategory(null);
                                            } else {
                                                setSelectedCategory(data.category);
                                                toast.info(`Filtered by category: ${data.category}`);
                                            }
                                        }}
                                    >
                                        {filteredCategories.map((entry, idx) => (
                                            <Cell
                                                key={idx}
                                                fill={COLORS[idx % COLORS.length]}
                                                style={{
                                                    cursor: "pointer",
                                                    opacity: selectedCategory && selectedCategory !== entry.category ? 0.4 : 1
                                                }}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" align="center" layout="horizontal" />
                                </PieChart>
                            </ResponsiveContainer>

                        </div>
                    </div>
                )}

                {/* Recent Activity */}
                {recent.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-header bg-light rounded-top-4">
                            <h5 className="mb-0 text-danger">Recent Amalan Baik</h5>
                        </div>
                        <ul className="list-group list-group-flush">
                            {recent.map((r, idx) => (
                                <li key={idx} className="list-group-item">
                                    <div className="d-flex justify-content-between">
                                        <span>
                                            <strong>{r.student_name}</strong> ({r.class_name}) - {r.category}
                                        </span>
                                        <small className="text-muted">{r.date}</small>
                                    </div>
                                    {r.remarks && <div className="text-muted small">{r.remarks}</div>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {loading && <div className="text-center text-muted">Loading...</div>}
            </div>
        </Fragment>
    );
};

export default AmalanBaikDashboard;
